import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Loader2, FileText, CheckCircle, Download, PenTool, ShieldCheck } from 'lucide-react';
import { getEduProContract, signEduProContract } from '../services/api';
import { downloadAmortizationPdf } from '../lib/eduproPdf';

export const EduProContract = ({ applicationId, onClose, onSigned, readOnly = false }) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: review, 2: success
  const [name, setName] = useState('');
  const [accept, setAccept] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getEduProContract(applicationId)
      .then((r) => {
        setContract(r.data);
        setName(r.data.deudor?.name || '');
        if (r.data.signed) setStep(2);
      })
      .catch((e) => setError(e.response?.data?.detail || 'No se pudo cargar el contrato'))
      .finally(() => setLoading(false));
  }, [applicationId]);

  const money = (v) =>
    new Intl.NumberFormat(contract?.locale || 'es-MX', {
      style: 'currency', currency: contract?.currency || 'MXN', maximumFractionDigits: 2,
    }).format(v || 0);

  const handleSign = async () => {
    if (!accept) { setError('Debes aceptar los términos del contrato'); return; }
    if (!name.trim()) { setError('Escribe tu nombre completo como firma'); return; }
    setSigning(true);
    setError('');
    try {
      const res = await signEduProContract(applicationId, { signature_name: name.trim(), device_info: navigator.userAgent });
      setContract(res.data);
      setStep(2);
      if (onSigned) onSigned(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al firmar el contrato');
    } finally {
      setSigning(false);
    }
  };

  const handlePdf = () => {
    if (!contract) return;
    downloadAmortizationPdf({
      principal: contract.principal, currency: contract.currency, locale: contract.locale,
      termMonths: contract.term_months, monthlyRate: contract.monthly_rate, priceUsd: contract.price_usd,
      schedule: contract.schedule, payment: contract.monthly_payment, total: contract.total_payment, interest: contract.total_interest,
      title: contract.signed ? 'Contrato de Financiamiento EduPro' : 'Tabla de Amortización EduPro',
      subtitle: `${contract.product} · ${contract.term_months} meses`,
      parties: { deudor: contract.deudor?.name, aval: contract.aval?.name },
      contractId: contract.contract_id, signatureName: contract.signed ? contract.signature_name : null, signedAt: contract.signed_at,
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 sm:px-6 pt-5">
          <DialogTitle className="flex items-center gap-2 text-[#0A2463]">
            <FileText className="w-5 h-5 text-[#E91E8C]" />
            {step === 1 ? 'Contrato de Financiamiento EduPro' : '¡Contrato firmado!'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#0A2463] mx-auto" /></div>
        ) : !contract ? (
          <div className="p-6 text-center text-red-600 text-sm">{error || 'Contrato no disponible'}</div>
        ) : (
          <div className="px-5 sm:px-6 pb-6">
            {error && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg mb-4" data-testid="contract-error">{error}</div>}

            {step === 1 && (
              <>
                {/* Summary */}
                <div className="bg-[#0A2463] text-white rounded-2xl p-5 grid grid-cols-2 gap-4 mb-5">
                  <div><p className="text-white/60 text-xs">Monto financiado</p><p className="text-xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{money(contract.principal)}</p></div>
                  <div><p className="text-white/60 text-xs">Pago mensual</p><p className="text-xl font-black text-[#E91E8C]" style={{ fontFamily: "'Outfit', sans-serif" }}>{money(contract.monthly_payment)}</p></div>
                  <div><p className="text-white/60 text-xs">Plazo / Tasa</p><p className="font-semibold">{contract.term_months} meses · {contract.monthly_rate_percent}%</p></div>
                  <div><p className="text-white/60 text-xs">Total a pagar</p><p className="font-semibold">{money(contract.total_payment)}</p></div>
                </div>

                {/* Parties */}
                <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
                  <div className="border border-slate-200 rounded-xl p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Deudor</p>
                    <p className="font-semibold text-slate-800">{contract.deudor?.name}</p>
                    <p className="text-xs text-slate-500">{contract.deudor?.national_id}</p>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Aval / Co-deudor</p>
                    <p className="font-semibold text-slate-800">{contract.aval?.name}</p>
                    <p className="text-xs text-slate-500">{contract.aval?.national_id} {contract.aval?.relationship ? `· ${contract.aval.relationship}` : ''}</p>
                  </div>
                </div>

                {/* Amortization table */}
                <p className="text-sm font-semibold text-[#0A2463] mb-2">Tabla de amortización</p>
                <div className="border border-slate-200 rounded-xl overflow-hidden mb-2">
                  <div className="max-h-52 overflow-y-auto">
                    <table className="w-full text-xs" data-testid="contract-amortization-table">
                      <thead className="bg-slate-100 text-slate-500 sticky top-0">
                        <tr><th className="py-2 px-2 text-left">#</th><th className="py-2 px-2 text-right">Pago</th><th className="py-2 px-2 text-right">Capital</th><th className="py-2 px-2 text-right">Interés</th><th className="py-2 px-2 text-right">Saldo</th></tr>
                      </thead>
                      <tbody>
                        {contract.schedule.map((s) => (
                          <tr key={s.month} className="border-t border-slate-100">
                            <td className="py-1.5 px-2 text-slate-500">{s.month}</td>
                            <td className="py-1.5 px-2 text-right font-medium">{money(s.payment)}</td>
                            <td className="py-1.5 px-2 text-right text-slate-500">{money(s.principal)}</td>
                            <td className="py-1.5 px-2 text-right text-slate-500">{money(s.interest)}</td>
                            <td className="py-1.5 px-2 text-right text-slate-500">{money(s.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Button onClick={handlePdf} variant="ghost" size="sm" className="text-[#0A2463] mb-5" data-testid="contract-download-pdf">
                  <Download className="w-4 h-4 mr-1.5" /> Descargar tabla en PDF
                </Button>

                {/* Clauses */}
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded-xl p-4 mb-5 space-y-1 max-h-32 overflow-y-auto">
                  <p><strong>PRIMERA.</strong> Ed.Fi otorga a EL DEUDOR un financiamiento por {money(contract.principal)} destinado al programa EduPro Global Education.</p>
                  <p><strong>SEGUNDA.</strong> EL DEUDOR pagará en {contract.term_months} mensualidades de {money(contract.monthly_payment)} a una tasa de {contract.monthly_rate_percent}% mensual.</p>
                  <p><strong>TERCERA.</strong> EL AVAL se constituye como obligado solidario del cumplimiento total de este contrato.</p>
                  <p><strong>CUARTA.</strong> EL DEUDOR podrá liquidar anticipadamente sin penalización alguna.</p>
                  <p><strong>QUINTA.</strong> Las partes autorizan el reporte a sociedades de información crediticia.</p>
                </div>

                {/* Signature */}
                <div className="border-t border-slate-200 pt-5">
                  {readOnly ? (
                    <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-500 text-center" data-testid="contract-readonly-note">
                      Vista de administrador (solo lectura). La firma corresponde al solicitante.
                    </div>
                  ) : (
                    <>
                      <Label className="flex items-center gap-1.5"><PenTool className="w-4 h-4 text-[#E91E8C]" /> Firma (escribe tu nombre completo)</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre completo" className="mt-2 text-lg" style={{ fontFamily: 'cursive' }} data-testid="contract-signature-input" />
                      <label className="flex items-start gap-2 mt-3 cursor-pointer">
                        <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="contract-accept" className="mt-0.5" />
                        <span className="text-xs text-slate-500">He leído y acepto los términos del contrato. Esta firma digital tiene la misma validez legal que una firma autógrafa.</span>
                      </label>
                      <Button onClick={handleSign} disabled={signing || !accept || !name.trim()} className="w-full mt-4 bg-[#E91E8C] hover:bg-[#c21473] text-white py-6 rounded-xl" data-testid="contract-sign-submit">
                        {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firmando...</> : 'Firmar contrato'}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}

            {step === 2 && (
              <div className="text-center py-6 space-y-5" data-testid="contract-signed-view">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto"><CheckCircle className="w-10 h-10 text-emerald-600" /></div>
                <div>
                  <h3 className="text-2xl font-black text-emerald-600" style={{ fontFamily: "'Outfit', sans-serif" }}>¡Contrato firmado!</h3>
                  <p className="text-slate-600 mt-1 text-sm">Firmado por <strong>{contract.signature_name}</strong>. Folio {contract.contract_id}.</p>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-5 max-w-sm mx-auto text-left flex items-center gap-3">
                  <ShieldCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                  <p className="text-xs text-slate-600">Tu financiamiento EduPro está activo. Guarda tu contrato en PDF para tus registros.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={handlePdf} variant="outline" className="rounded-xl" data-testid="contract-download-signed">
                    <Download className="w-4 h-4 mr-2" /> Descargar contrato (PDF)
                  </Button>
                  <Button onClick={onClose} className="bg-[#0A2463] text-white rounded-xl" data-testid="contract-close">Listo</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EduProContract;
